import React, { useState, useEffect, useCallback } from 'react';
import MonthlyVacationDeclaration, { vacationMonthsForDates } from '../../components/employee/MonthlyVacationDeclaration';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';

import { timeEntryService } from '../../services/api/timeEntryService';
import { customerService } from '../../services/api/customerService';
import { settingsService } from '../../services/api';
import { parseTimeToMinutes, formatMinutes } from '../../utils/time';

/* ---------- date helpers ---------- */

const toDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Week-start for the given Date, honoring the configured start day
// ('monday' by default, 'sunday' when the admin configures it).
const weekStartOf = (date, startDay = 'monday') => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay(); // 0 = Sunday .. 6 = Saturday
  const diff = startDay === 'sunday' ? dow : (dow + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
};

const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return toDateString(dt);
};

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Weekday key for a 'YYYY-MM-DD' date, derived from the actual calendar day so
// labels stay correct regardless of which day the week starts on.
const dayKeyOf = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return DAY_KEYS[(new Date(y, m - 1, d).getDay() + 6) % 7];
};

/* ---------- styled ---------- */

const Page = styled.div`
  max-width: 900px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: var(--ve-ink-800);
  margin: 0 0 1rem;
`;

const Selectors = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  flex: 1;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ve-ink-600);
  margin-bottom: 0.35rem;
`;

const Select = styled.select`
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.25rem;
  font-size: 0.95rem;
  color: var(--ve-ink-800);
  background: #fff;
  &:focus { outline: none; border-color: var(--act-primary); }

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom on focus */
    min-height: 44px;
  }
`;

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 1rem 0;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.25rem;
  background: #fff;
  color: var(--ve-ink-800);
  cursor: pointer;
  font-size: 0.9rem;
  &:hover { background: var(--surface-subtle); }

  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

const WeekLabel = styled.div`
  font-weight: 600;
  color: var(--ve-ink-800);
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0;
  border: 1px solid #eef1f3;
  border-radius: 0.4rem;
  overflow: hidden;
`;

const Row = styled.div`
  display: contents;
`;

const DayCell = styled.div`
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${({ $status }) =>
    $status === 'challenged' ? '#fdECEC' : $status === 'approved' ? 'var(--surface-subtle)' : '#fff'};
`;

const DayName = styled.div`
  font-weight: 600;
  color: var(--ve-ink-800);
`;

const DayMeta = styled.div`
  font-size: 0.75rem;
  color: ${({ $status }) =>
    $status === 'challenged' ? 'var(--act-danger)' : $status === 'approved' ? 'var(--act-approve)' : 'var(--ve-ink-500)'};
`;

const InputCell = styled.div`
  padding: 0.55rem 1rem;
  border-bottom: 1px solid #eef1f3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: ${({ $status }) =>
    $status === 'challenged' ? '#fdECEC' : $status === 'approved' ? 'var(--surface-subtle)' : '#fff'};
`;

const TimeInput = styled.input`
  width: 90px;
  padding: 0.5rem;
  text-align: center;
  border: 1px solid ${({ $invalid }) => ($invalid ? 'var(--act-danger)' : 'var(--border-subtle)')};
  border-radius: 0.25rem;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  color: var(--ve-ink-800);
  &:disabled { background: var(--surface-subtle); color: var(--ve-ink-500); cursor: not-allowed; }
  &:focus { outline: none; border-color: var(--act-primary); }

  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  font-weight: 700;
  color: var(--ve-ink-800);
  background: var(--surface-subtle);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #fff;
    margin-top: 1rem;
    padding: 0.75rem 0 calc(0.75rem + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
  }
`;

// Green when the week is submitted and unchanged; orange while it is dirty or
// has never been submitted, to prompt the employee to save.
const SaveButton = styled.button`
  padding: 0.6rem 1.4rem;
  background: ${({ $submitted, theme }) => ($submitted ? theme.colors.success : theme.colors.accentDark)};
  color: #fff;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, filter 0.15s ease;
  &:hover { filter: brightness(0.94); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 48px;
  }
`;

const Alert = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  background: ${({ $type }) => ($type === 'error' ? 'var(--status-danger-plate)' : '#eafaf1')};
  color: ${({ $type }) => ($type === 'error' ? 'var(--act-danger)' : '#1e8449')};
  border: 1px solid ${({ $type }) => ($type === 'error' ? '#f5c6cb' : '#abebc6')};
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: var(--ve-ink-500);
  margin: 0.5rem 0 0;
`;

const Empty = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--ve-ink-500);
`;

/* ---------- component ---------- */

const WeeklyEntry = () => {
  const { t } = useTranslation();

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [workWeekStart, setWorkWeekStart] = useState('monday');
  const [weekStart, setWeekStart] = useState(toDateString(weekStartOf(new Date())));
  const [week, setWeek] = useState(null);
  const [inputs, setInputs] = useState({}); // date -> string
  const [invalid, setInvalid] = useState({}); // date -> bool

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load assigned customers + persisted selection on mount.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [custList, prefs, cfg] = await Promise.all([
          customerService.getEmployeeCustomers(),
          timeEntryService.getPreferences().catch(() => ({})),
          settingsService.getAppConfig().catch(() => ({ workWeekStart: 'monday' })),
        ]);
        if (!active) return;
        // Honor the configured week-start day; realign the current week to it.
        const startDay = cfg && cfg.workWeekStart === 'sunday' ? 'sunday' : 'monday';
        setWorkWeekStart(startDay);
        setWeekStart(toDateString(weekStartOf(new Date(), startDay)));
        const list = Array.isArray(custList) ? custList : [];
        setCustomers(list);

        // Auto-select: persisted default, else the only customer.
        let initialCustomer = '';
        if (prefs && prefs.defaultCustomerId && list.some((c) => c.id === prefs.defaultCustomerId)) {
          initialCustomer = String(prefs.defaultCustomerId);
        } else if (list.length === 1) {
          initialCustomer = String(list[0].id);
        }
        setCustomerId(initialCustomer);
        if (prefs && prefs.defaultProjectId) {
          setProjectId(String(prefs.defaultProjectId));
        }
      } catch (err) {
        if (active) setError(err.message || t('common.error'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  // Load projects whenever the customer changes; auto-select single/only.
  useEffect(() => {
    let active = true;
    if (!customerId) {
      setProjects([]);
      return undefined;
    }
    (async () => {
      try {
        const projList = await customerService.getCustomerProjects(customerId);
        if (!active) return;
        const list = Array.isArray(projList) ? projList : [];
        setProjects(list);
        setProjectId((prev) => {
          if (prev && list.some((p) => String(p.id) === String(prev))) return prev;
          if (list.length === 1) return String(list[0].id);
          return '';
        });
      } catch (err) {
        if (active) setProjects([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [customerId]);

  // Load the week whenever selection or week changes.
  const loadWeek = useCallback(async () => {
    if (!customerId) {
      setWeek(null);
      setInputs({});
      return;
    }
    try {
      setError(null);
      const params = { weekStart, customerId };
      if (projectId) params.projectId = projectId;
      const data = await timeEntryService.getWeek(params);
      setWeek(data);
      const nextInputs = {};
      data.days.forEach((d) => {
        nextInputs[d.date] = d.durationMinutes > 0 ? formatMinutes(d.durationMinutes) : '';
      });
      setInputs(nextInputs);
      setInvalid({});
    } catch (err) {
      setError(err.message || t('common.error'));
    }
  }, [customerId, projectId, weekStart, t]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  const persistSelection = (nextCustomer, nextProject) => {
    timeEntryService
      .updatePreferences({
        defaultCustomerId: nextCustomer ? Number(nextCustomer) : null,
        defaultProjectId: nextProject ? Number(nextProject) : null,
      })
      .catch(() => {});
  };

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    setCustomerId(value);
    setProjectId('');
    setSuccess(null);
    persistSelection(value, '');
  };

  const handleProjectChange = (e) => {
    const value = e.target.value;
    setProjectId(value);
    setSuccess(null);
    persistSelection(customerId, value);
  };

  const handleInputChange = (date, value) => {
    setInputs((prev) => ({ ...prev, [date]: value }));
    setSuccess(null);
    const parsed = parseTimeToMinutes(value);
    setInvalid((prev) => ({ ...prev, [date]: !!parsed.error }));
  };

  const shiftWeek = (days) => {
    setWeekStart((prev) => addDays(prev, days));
    setSuccess(null);
  };

  const goThisWeek = () => {
    setWeekStart(toDateString(weekStartOf(new Date(), workWeekStart)));
    setSuccess(null);
  };

  const weekTotalMinutes = week
    ? week.days.reduce((sum, d) => {
        const parsed = parseTimeToMinutes(inputs[d.date]);
        return sum + (parsed.error ? 0 : parsed.minutes);
      }, 0)
    : 0;

  const hasInvalid = Object.values(invalid).some(Boolean);

  // Dirty = any day's current parsed minutes differ from the loaded baseline.
  const isDirty = week
    ? week.days.some((d) => {
        const parsed = parseTimeToMinutes(inputs[d.date]);
        const current = parsed.error ? 0 : parsed.minutes;
        return current !== (d.durationMinutes || 0);
      })
    : false;

  // Green only when the backend has a submission for this week AND nothing has
  // changed since it was loaded/saved AND no field holds invalid input;
  // otherwise the button stays orange (an invalid entry must not read as saved).
  const weekSubmitted = !!(week && week.submitted && !isDirty && !hasInvalid);

  const handleSave = async () => {
    if (!customerId || !week) return;
    if (hasInvalid) {
      setError(t('employee.week.invalidTime'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const days = week.days
        .filter((d) => d.editable)
        .map((d) => {
          const parsed = parseTimeToMinutes(inputs[d.date]);
          return { date: d.date, durationMinutes: parsed.error ? 0 : parsed.minutes };
        });
      const payload = { customerId: Number(customerId), weekStart, days };
      if (projectId) payload.projectId = Number(projectId);
      const data = await timeEntryService.saveWeek(payload);
      setWeek(data);
      const nextInputs = {};
      data.days.forEach((d) => {
        nextInputs[d.date] = d.durationMinutes > 0 ? formatMinutes(d.durationMinutes) : '';
      });
      setInputs(nextInputs);
      setSuccess(t('employee.week.saved'));
    } catch (err) {
      setError(err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Empty>{t('common.loading')}</Empty>;
  }

  return (
    <Page>
      <Title>{t('employee.week.title')}</Title>

      {error && <Alert $type="error">{error}</Alert>}
      {success && <Alert $type="success">{success}</Alert>}

      {customers.length === 0 ? (
        <Card>
          <Empty>{t('employee.week.noCustomers')}</Empty>
        </Card>
      ) : (
        <Card>
          <Selectors>
            <Field>
              <Label htmlFor="customer">{t('employee.week.customer')}</Label>
              <Select id="customer" value={customerId} onChange={handleCustomerChange}>
                <option value="">{t('employee.week.selectCustomer')}</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="project">{t('employee.week.project')}</Label>
              <Select
                id="project"
                value={projectId}
                onChange={handleProjectChange}
                disabled={!customerId}
              >
                <option value="">{t('employee.week.noProject')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
          </Selectors>

          {!customerId ? (
            <Empty>{t('employee.week.selectPrompt')}</Empty>
          ) : (
            <>
              <WeekNav>
                <NavButton onClick={() => shiftWeek(-7)}>
                  <FiChevronLeft /> {t('employee.week.previousWeek')}
                </NavButton>
                <WeekLabel>
                  <FiCalendar />
                  {week
                    ? t('employee.week.weekOf', { start: week.weekStart, end: week.weekEnd })
                    : ''}
                </WeekLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <NavButton onClick={goThisWeek}>{t('employee.week.thisWeek')}</NavButton>
                  <NavButton onClick={() => shiftWeek(7)}>
                    {t('employee.week.nextWeek')} <FiChevronRight />
                  </NavButton>
                </div>
              </WeekNav>

              {week && (
                <>
                  {vacationMonthsForDates(week.days.map((day) => day.date)).map((period) => (
                    <MonthlyVacationDeclaration key={`${period.year}-${period.month}`} {...period} />
                  ))}
                  <Grid>
                    {week.days.map((d) => (
                      <Row key={d.date}>
                        <DayCell $status={d.status}>
                          <DayName>{t(`days.${dayKeyOf(d.date)}`)}</DayName>
                          <DayMeta $status={d.status}>
                            {d.date}
                            {!d.editable ? ` · ${t('employee.week.approvedLocked')}` : ''}
                            {d.status === 'challenged' ? ` · ${t('status.challenged')}` : ''}
                          </DayMeta>
                        </DayCell>
                        <InputCell $status={d.status}>
                          <TimeInput
                            type="text"
                            inputMode="numeric"
                            placeholder="0:00"
                            value={inputs[d.date] ?? ''}
                            $invalid={invalid[d.date]}
                            disabled={!d.editable}
                            onChange={(e) => handleInputChange(d.date, e.target.value)}
                            aria-label={t(`days.${dayKeyOf(d.date)}`)}
                          />
                        </InputCell>
                      </Row>
                    ))}
                    <TotalRow>
                      <span>{t('employee.week.weekTotal')}</span>
                      <span>{formatMinutes(weekTotalMinutes)}</span>
                    </TotalRow>
                  </Grid>
                  <Hint>{t('employee.week.hint')}</Hint>
                  <Actions>
                    <SaveButton
                      onClick={handleSave}
                      disabled={saving || hasInvalid}
                      $submitted={weekSubmitted}
                    >
                      {saving ? t('employee.week.saving') : t('employee.week.save')}
                    </SaveButton>
                  </Actions>
                </>
              )}
            </>
          )}
        </Card>
      )}
    </Page>
  );
};

export default WeeklyEntry;
